import { useEffect, useRef, useState } from "react";
import LogoWhite10 from "../../../Media/PNG/LogoWhite10.svg";
import LogoBlue from "../../../Media/PNG/LogoBlue.svg";
import blueBg from "../../../Media/SVG/coupon_blue.svg";
import greenBg from "../../../Media/SVG/coupon_green.svg";
import redBg from "../../../Media/SVG/coupon_red.svg";
import SVG from "../../SVG";
import Custom from "../../Buttons/Custom";
import { useNavigate } from "react-router-dom";

export default function Coupon({
  color,
  code,
  title = "",
  challenge = "",
  expiration = "",
  setAccLoading,
}) {
  const copiedTimeoutRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [isDark, setIsDark] = useState(
    typeof document !== "undefined" &&
      document.documentElement.classList.contains("dark"),
  );

  // loading states (logo + bg)
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [bgLoaded, setBgLoaded] = useState(false);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const html = document.documentElement;
    const observer = new MutationObserver(() => {
      setIsDark(html.classList.contains("dark"));
    });

    observer.observe(html, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  const bgMap = {
    blue: blueBg,
    green: greenBg,
    red: redBg,
  };
  const bg = bgMap[color];

  const logoSrc = isDark ? LogoWhite10 : LogoBlue;

  // preload background image
  useEffect(() => {
    setBgLoaded(false);

    const img = new Image();
    img.src = bg;

    img.onload = () => setBgLoaded(true);
    img.onerror = () => setBgLoaded(true);
  }, [bg]);

  // when theme changes, logo src changes -> reset logoLoaded
  useEffect(() => {
    setLogoLoaded(false);
  }, [logoSrc]);

  useEffect(() => {
    if (typeof setAccLoading !== "function") return;
    setAccLoading(!(logoLoaded && bgLoaded));
  }, [logoLoaded, bgLoaded, setAccLoading]);

  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }
    };
  }, []);

  const handleCopy = async () => {
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);

      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }

      copiedTimeoutRef.current = setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy coupon code:", err);
    }
  };

  return (
    <div
      className="max-w-96 w-full overflow-hidden flex drop-shadow-md transition-opacity duration-300"
      style={{
        // opacity: logoLoaded && bgLoaded ? 1 : 0,
        backgroundImage: `url(${bg})`,
        backgroundSize: "100% 100%",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        aspectRatio: "343 / 171",
      }}
    >
      <div className="w-4/5 h-full p-3 rounded-r-2xl flex flex-col justify-between bg-zi-bg2-light dark:bg-zi-inputbg-dark">
        <div className="w-full flex justify-between items-start">
          <img
            src={logoSrc}
            className="w-7"
            alt="لوگو"
            onLoad={() => setLogoLoaded(true)}
            onError={() => setLogoLoaded(true)}
          />

          <div className="flex flex-col items-end">
            <div className="flex gap-2.5 justify-start items-center">
              <button
                type="button"
                onClick={handleCopy}
                className="p-1"
                aria-label="کپی کد تخفیف"
              >
                <SVG
                  name={copied ? "tick" : "copy"}
                  classname={
                    copied
                      ? "fill-zi-text-light dark:fill-zi-text-dark stroke-zi-text-dark dark:stroke-zi-text-light cursor-pointer size-3"
                      : "stroke-zi-text-light dark:stroke-zi-text-dark cursor-pointer"
                  }
                  style={{}}
                />
              </button>
              <span className="font-medium text-zi-text-light dark:text-zi-text-dark">
                {code}
              </span>
            </div>
            <p className="text-xs text-zi-text-light/50 dark:text-zi-text-dark/50">
              {title}
            </p>
          </div>
        </div>

        <div className="w-full flex flex-col gap-3">
          <div className="w-full flex justify-between text-xs text-zi-text-light/50 dark:text-zi-text-dark/50">
            <span>{challenge}</span>
            <span>{expiration}</span>
          </div>

          <Custom
            onClick={() => {
              if (color !== "green") {
                return;
              }
              navigate("/purchase");
            }}
            text={
              color === "green"
                ? "خرید چالش با کد تخفیف"
                : color === "blue"
                  ? "قبلا استفاده شده"
                  : color === "red"
                    ? "منقضی شده"
                    : ""
            }
            href=""
            className="!rounded-full !py-1.5 border border-dashed border-zi-border-dark/50 dark:border-zi-border-light/30 !text-sm text-zi-text-light/50 dark:text-zi-text-dark mb:hocus:border-zi-border-dark/100 mb:dark:hocus:border-zi-border-light/100"
          />
        </div>
      </div>

      <div className="relative w-1/5 h-full flex flex-col items-center justify-center">
        <span className="block -rotate-90 whitespace-nowrap text-zi-text-dark text-xl font-medium select-none transform">
          کد تخفیف
        </span>
        <div className="absolute bottom-2.5">
          <SVG
            name="saleForCoupon"
            classname="stroke-zi-text-dark/10 size-7"
            style={{}}
          />
        </div>
        <div className="absolute top-2.5">
          <SVG
            name="saleForCoupon"
            classname="stroke-zi-text-dark/10 size-7"
            style={{}}
          />
        </div>
      </div>
    </div>
  );
}
